import { HIERARCHY_AUDIT_ACTIONS } from "../constants/hierarchyAuditActions";

export const DEFAULT_ORG_SETTINGS = {
  currency: null,
  approvalSlaDays: 3
};

/** Reports sidebar/nav is visible; /goals_kpi/reports is also reachable by direct URL. */
export const REPORTS_NAV_VISIBLE = true;

/** Temporarily hide Score Formula Builder on Metadata Config until backend wiring exists. */
export const FORMULA_BUILDER_VISIBLE = false;

/** Temporarily hide Export PDF / Share mock actions (dashboard, Level View) until real export exists. */
export const EXPORT_SHARE_VISIBLE = false;

export const REPORT_FREQUENCIES = ["DAILY", "WEEKLY", "MONTHLY", "QUARTERLY", "ANNUALLY"];

export { HIERARCHY_AUDIT_ACTIONS };

/** Backend BusinessValidationException keys surfaced on hierarchy attribute PUT. */
export const HIERARCHY_VALIDATION_ERROR_KEYS = {
  COMMITTED_REMARK_REQUIRED: "hierarchy.validation.error.attribute.committed.remark.required",
  TASK_BUDGET_EXCEEDS_PARENT: "hierarchy.validation.error.task.budget.exceeds.parent",
  DELIVERY_BUDGET_BELOW_CHILD_ALLOCATIONS: "hierarchy.validation.error.delivery.budget.below.child.allocations"
};

/** Resolve a hierarchy validation error key from an axios rejection (400 Problem body). */
export const extractHierarchyValidationKey = (axiosError) => {
  const data = axiosError?.response?.data;
  if (!data) {
    return null;
  }
  const msg = String(data.message || data.detail || data.title || "");
  const {
    COMMITTED_REMARK_REQUIRED,
    TASK_BUDGET_EXCEEDS_PARENT,
    DELIVERY_BUDGET_BELOW_CHILD_ALLOCATIONS
  } = HIERARCHY_VALIDATION_ERROR_KEYS;
  if (msg.includes(COMMITTED_REMARK_REQUIRED) || msg.includes("committed_remark is required")) {
    return COMMITTED_REMARK_REQUIRED;
  }
  if (msg.includes(TASK_BUDGET_EXCEEDS_PARENT)) {
    return TASK_BUDGET_EXCEEDS_PARENT;
  }
  if (msg.includes(DELIVERY_BUDGET_BELOW_CHILD_ALLOCATIONS)) {
    return DELIVERY_BUDGET_BELOW_CHILD_ALLOCATIONS;
  }
  return null;
};

/** Raw backend message for a hierarchy validation rejection — this is the text to actually show
 *  the user for amount-specific errors (budget cap messages), since it already contains the real
 *  number and a static translated string can't reconstruct that. */
export const extractHierarchyValidationMessage = (axiosError) => {
  const data = axiosError?.response?.data;
  if (!data) {
    return null;
  }
  return data.message || data.detail || data.title || null;
};

export const AUDIT_ACTIONS = HIERARCHY_AUDIT_ACTIONS;

export const AUDIT_ENTITY_TYPES = [
  "GOAL_NODE",
  "ASSIGNMENT",
  "APPROVAL",
  "BUDGET",
  "EVIDENCE",
  "SESSION",
  "REPORT"
];

/** Admin hub summary card — no hierarchy permissions API yet. */
export const ADMIN_ROLES = [];

/** Champion keeps the full Department Head-style dashboard, including approval widgets. */
const CHAMPION_DASHBOARD_LAYOUT = [
  { widgetId: "divisionScore", span: 3 },
  { widgetId: "onTrackStrategicInitiatives", span: 3 },
  { widgetId: "onTrackTasks", span: 3 },
  { widgetId: "pendingApprovalsCount", span: 3 },
  { widgetId: "myEscalationsSummary", span: 12 },
  { widgetId: "strategicObjectives", span: 8 },
  { widgetId: "approvalsAwaiting", span: 4 },
  // divisionActivity is wired to GET /api/audit (ownerId + planId, no nodeId).
  // Hidden from the live Champion dashboard until we decide to show it.
  // { widgetId: "divisionActivity", span: 6 },
  { widgetId: "teamMembers", span: 6 },
  { widgetId: "myNotifications", span: 6 }
];

/** Department Head: same as Champion, without approval widgets (no Approvals nav access). */
const DEPT_HEAD_APPROVAL_WIDGET_IDS = new Set(["pendingApprovalsCount", "approvalsAwaiting"]);
const DEPT_HEAD_DASHBOARD_LAYOUT = CHAMPION_DASHBOARD_LAYOUT.filter(
  (widget) => !DEPT_HEAD_APPROVAL_WIDGET_IDS.has(widget.widgetId)
);

export const DASHBOARD_PERSONAS = [
  {
    id: "executive",
    labelKey: "GoalsAndKpi.persona.executive.label",
    subtitleKey: "GoalsAndKpi.persona.executive.subtitle.label",
    enabled: true,
    dataScope: "ORG",
    layout: [
      // 1. Overview stats
      { widgetId: "corporateScore", span: 3 },
      // Replaced by the level-specific tiles below - commented out, not removed.
      // { widgetId: "onTrackCount", span: 3 },
      // { widgetId: "atRiskCount", span: 3 },
      // { widgetId: "offTrackCount", span: 3 },
      { widgetId: "fullyAchievedSO", span: 3 },
      { widgetId: "fullyAchievedKRG", span: 3 },
      { widgetId: "onTrackSI", span: 3 },
      // 2. Pillar progress
      { widgetId: "pillarScorecards", span: 12 },
      // 3. Attention - items needing attention and ownership focus
      { sectionLabelKey: "GoalsAndKpi.dashboard.section.attention.label" },
      { widgetId: "criticalInitiatives", span: 6 },
      { widgetId: "accountabilityFocus", span: 6 },
      // 4. Budget - summary then the two Top 5 drill-down lists
      { sectionLabelKey: "GoalsAndKpi.dashboard.section.budget.label" },
      { widgetId: "budgetSummary", span: 12 },
      { widgetId: "budgetAtRisk", span: 6 },
      { widgetId: "budgetUnderUtilised", span: 6 },
      // 5. Escalations
      { sectionLabelKey: "GoalsAndKpi.dashboard.section.escalations.label" },
      { widgetId: "myEscalationsSummary", span: 12 },
      { widgetId: "topEscalationRecipients", span: 6 }
    ]
  },
  {
    id: "depthead",
    labelKey: "GoalsAndKpi.persona.depthead.label",
    subtitleKey: "GoalsAndKpi.persona.depthead.subtitle.label",
    enabled: true,
    dataScope: "DIVISION",
    layout: DEPT_HEAD_DASHBOARD_LAYOUT
  },
  {
    id: "champion",
    labelKey: "GoalsAndKpi.persona.champion.label",
    subtitleKey: "GoalsAndKpi.persona.champion.subtitle.label",
    enabled: true,
    dataScope: "DIVISION",
    layout: CHAMPION_DASHBOARD_LAYOUT
  },
  {
    id: "divisionhead",
    labelKey: "GoalsAndKpi.persona.divisionhead.label",
    subtitleKey: "GoalsAndKpi.persona.divisionhead.subtitle.label",
    enabled: true,
    dataScope: "TEAM",
    layout: [
      { widgetId: "myAvgScore", span: 3 },
      { widgetId: "atRiskItemsCount", span: 3 },
      { widgetId: "activeInitiativesCount", span: 3 },
      // { widgetId: "pendingApprovalsCount", span: 3 },
      { widgetId: "teamBudgetOverview", span: 12 },
      { widgetId: "myEscalationsSummary", span: 12 },
      { widgetId: "myInitiatives", span: 6 },
      { widgetId: "teamMembers", span: 6 },
      // { widgetId: "approvalsAwaiting", span: 4 },
      { widgetId: "teamTasksBudgetDetail", span: 12 },
      
    ]
  },
  {
    id: "taskowner",
    labelKey: "GoalsAndKpi.persona.taskowner.label",
    subtitleKey: "GoalsAndKpi.persona.taskowner.subtitle.label",
    enabled: true,
    dataScope: "SELF",
    layout: [
      { widgetId: "overdueTasksCount", span: 4 },
      // { widgetId: "myGoalProgress", span: 4 },
      { widgetId: "pendingSubmissionCount", span: 4 },
      { widgetId: "completedAtLevelCount", span: 4 },
      { widgetId: "myEscalationsSummary", span: 12 },
      { widgetId: "myTasksCards", span: 12 },
      // dueSoon renders Due Soon + My Notifications in one two-column row (same as myTasksCards).
      { widgetId: "dueSoon", span: 12 }
      // { widgetId: "myGoalSummary", span: 6 },
      // { widgetId: "myNotifications", span: 12 } - now nested inside dueSoon
    ]
  },
  {
    id: "admin",
    labelKey: "GoalsAndKpi.persona.admin.label",
    subtitleKey: "GoalsAndKpi.persona.admin.subtitle.label",
    enabled: true,
    dataScope: "ORG",
    layout: [
      { widgetId: "corporateScore", span: 3 },
      // Replaced by the level-specific tiles below - commented out, not removed.
      // { widgetId: "onTrackCount", span: 3 },
      // { widgetId: "atRiskCount", span: 3 },
      // { widgetId: "offTrackCount", span: 3 },
      { widgetId: "fullyAchievedSO", span: 3 },
      { widgetId: "fullyAchievedKRG", span: 3 },
      { widgetId: "onTrackSI", span: 3 },
      { widgetId: "pillarScorecards", span: 12 },
      { widgetId: "myEscalationsSummary", span: 12 },
      { widgetId: "topEscalationRecipients", span: 6 }
    ]
  }
];

/** JWT dashboard views — see hierarchyDashboardViews.js */
export { KPI_DASHBOARD_VIEWS } from "./hierarchyDashboardViews";

/** Backend max length for hierarchy node / strategic plan "description" — enforced via BusinessValidationException. */
export const DESCRIPTION_MAX_LENGTH = 1000;
